import * as z from "zod";
import { userFacingError } from "../authorization";
import { zql } from "../generatedDrizzleSchema";
import { isSupplierAvailableFromGrants } from "../rbac/crossResourceAvailability";
import {
  buildUserGroupMemberships,
  grantsForScope,
} from "../rbac/grantQueries";
import { checkServiceAccountGrant } from "../rbac/serviceAccountGrantAuthorization";
import { minimalSupplierGrants } from "../rbac/spaceSupplierAutoGrant";
import {
  grantEntry,
  grantKey,
  insertGrantsDeduped,
  principalKey,
  validateGrants,
} from "../rbac/validation";
import { mutator } from "./mutator";

export const setWorkflowAppBackingSchema = z.object({
  connectedAppId: z.string(),
  workflowId: z.string(),
  grants: z.array(grantEntry).optional(),
});

export const setWorkflowAppBacking = mutator({
  schema: setWorkflowAppBackingSchema,
  resolve: {
    workflow: { kind: "workflow", from: "workflowId" },
    app: { kind: "connectedApp", from: "connectedAppId" },
  },
  permissions: [
    {
      permission: "space:write",
      scopeId: ({ args }) => args.workflow.spaceId,
    },
    {
      permission: "connected_app:write",
      scopeId: ({ args }) => args.app.id,
    },
  ],
  fn: async ({ tx, ctx, args: { app, workflow, grants } }) => {
    if (app.identifier !== "workflow") {
      throw userFacingError("This connector is not workflow-backed.");
    }
    // urlPattern, baseUrl, and the secret token are written server-side from the workflow's space — a client-supplied urlPattern would leak the proxy-injected token to an attacker URL.
    await tx.mutate.connectedApps.update({
      id: app.id,
      backingWorkflowId: workflow.id,
    });
    if (!grants?.length) return;
    const [spaceGrants, connectorGrants] = await Promise.all([
      tx.run(grantsForScope(workflow.spaceId, ctx.tenantId)),
      tx.run(grantsForScope(app.id, ctx.tenantId)),
    ]);
    const allowed = new Set(
      minimalSupplierGrants("space_uses_connected_apps", spaceGrants).map(
        grantKey
      )
    );
    const resolved = validateGrants(grants, "connected_app", []);
    if (resolved.some(({ input }) => !allowed.has(grantKey(input)))) {
      throw userFacingError(
        "Connector access must match the backing workflow’s space access."
      );
    }
    const userIds = grants.flatMap((grant) =>
      grant.userId ? [grant.userId] : []
    );
    const memberships = buildUserGroupMemberships(
      userIds.length
        ? await tx.run(
            zql.roleGrants
              .where("tenantId", ctx.tenantId)
              .where("userId", "IN", userIds)
          )
        : []
    );
    const additions = resolved.filter(
      ({ input }) =>
        !isSupplierAvailableFromGrants(
          "space_uses_connected_apps",
          spaceGrants.filter(
            (grant) => principalKey(grant) === principalKey(input)
          ),
          connectorGrants,
          memberships
        )
    );
    for (const { input, role } of additions) {
      if (!input.serviceAccountId) continue;
      const denial = await checkServiceAccountGrant({
        tx,
        ctx,
        scopeId: app.id,
        role,
      });
      if (denial)
        throw userFacingError(
          "You can’t grant a service account connector permissions you don’t hold."
        );
    }
    await insertGrantsDeduped(
      tx,
      additions.map(({ input }) => input),
      app.id,
      ctx.tenantId
    );
  },
});
